import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquareQuote } from 'lucide-react';
import {
  createCannedResponse,
  deleteCannedResponse,
  listCannedResponses,
  updateCannedResponse,
  type CannedResponse,
  type CannedScope,
} from '../api/cannedResponses';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { ApiError } from '../api/client';
import { useT } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import { Dialog } from '../components/Dialog';

const QK = ['canned_responses'] as const;

export function CannedResponsesPage() {
  const queryClient = useQueryClient();
  const session = useAuthStore((s) => s.session);
  const isAdmin = session?.operator.role === 'admin';
  const myId = session?.operator.id;
  const [editing, setEditing] = useState<CannedResponse | 'new' | null>(null);
  const t = useT();

  const { data: list, isLoading, error } = useQuery<CannedResponse[], Error>({
    queryKey: QK,
    queryFn: () => listCannedResponses(getApiClient()),
    refetchOnWindowFocus: false,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteCannedResponse(getApiClient(), id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QK }),
  });

  function canEdit(cr: CannedResponse): boolean {
    if (cr.scope === 'shared') return !!isAdmin;
    return cr.operatorId === myId;
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t('canned.title')}
        action={
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="rounded-md bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            {t('canned.create')}
          </button>
        }
      />

      {isLoading && <SkeletonList ariaLabel={t('common.loading')} />}
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.errorPrefix')}: {error.message}
        </p>
      )}

      {list && list.length === 0 && (
        <EmptyState
          icon={MessageSquareQuote}
          title={t('canned.empty')}
          actionLabel={t('canned.create')}
          onAction={() => setEditing('new')}
        />
      )}

      {list && list.length > 0 && (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">{t('canned.titleField')}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t('canned.shortcut')}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t('canned.scope')}</th>
                <th className="px-4 py-2.5 text-right font-medium">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((cr) => (
                <tr key={cr.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-900">{cr.title}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">
                    {cr.shortcut ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <ScopePill scope={cr.scope} t={t} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canEdit(cr) ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditing(cr)}
                          className="mr-3 text-xs text-slate-600 hover:text-slate-900"
                        >
                          {t('canned.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(t('canned.confirmDelete'))) {
                              deleteMut.mutate(cr.id);
                            }
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          {t('canned.delete')}
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">{t('common.readonly')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          existing={editing === 'new' ? null : editing}
          isAdmin={!!isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: QK });
            setEditing(null);
          }}
        />
      )}
    </PageContainer>
  );
}

function ScopePill({ scope, t }: { scope: CannedScope; t: ReturnType<typeof useT> }) {
  const shared = scope === 'shared';
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ' +
        (shared ? 'bg-accent-50 text-accent-700' : 'bg-slate-100 text-slate-600')
      }
    >
      {shared ? t('canned.scope.shared') : t('canned.scope.personal')}
    </span>
  );
}

interface EditModalProps {
  existing: CannedResponse | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function EditModal({ existing, isAdmin, onClose, onSaved }: EditModalProps) {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [shortcut, setShortcut] = useState(existing?.shortcut ?? '');
  const [scope, setScope] = useState<CannedScope>(existing?.scope ?? 'personal');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const t = useT();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const shortcutValue = shortcut.trim() === '' ? null : shortcut.trim();
      if (existing) {
        await updateCannedResponse(getApiClient(), existing.id, {
          title,
          body,
          shortcut: shortcutValue,
        });
      } else {
        await createCannedResponse(getApiClient(), {
          title,
          body,
          ...(shortcutValue !== null ? { shortcut: shortcutValue } : {}),
          scope,
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('canned.saveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={existing ? t('canned.editTitle') : t('canned.newTitle')}
    >
      <form onSubmit={handleSubmit} id="canned-form" className="space-y-3">
        <label className="block">
          <span className="text-xs text-slate-500">{t('canned.titleField')}</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
            maxLength={120}
            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-500">{t('canned.shortcutHint')}</span>
          <input
            type="text"
            value={shortcut}
            onChange={(e) => setShortcut(e.currentTarget.value)}
            maxLength={32}
            placeholder="/welcome"
            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-500">{t('canned.body')}</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            required
            maxLength={4000}
            rows={6}
            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
          />
        </label>

        {!existing && isAdmin && (
          <label className="block">
            <span className="text-xs text-slate-500">{t('canned.scope')}</span>
            <select
              value={scope}
              onChange={(e) => setScope(e.currentTarget.value as CannedScope)}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
            >
              <option value="personal">{t('canned.scope.personalLong')}</option>
              <option value="shared">{t('canned.scope.sharedLong')}</option>
            </select>
          </label>
        )}

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-accent-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
